import React from 'react';

import {
  fetchArtStyle,
  listArtStyles,
  MASCOT_AUTO,
  type ArtStyleMascot,
  type ArtStyleSummary,
  type MascotPick,
} from './art-style-api';

export interface StyleChoices {
  /** The brand's styles a draw can use (every status, each with an extracted definition), the active one first;
   *  empty while reading, when it has none, or when its first read failed (a later failed read keeps the list). */
  styles: ArtStyleSummary[];
  /** The style a draw uses unless another is picked: the active (primary) one; none when the brand has no primary. */
  defaultId: string | undefined;
  /** The brand's list read has settled (answered, or failed: a later read keeps the last answer), so an empty
   *  `styles` means "none usable" rather than "still reading". True with no brand (nothing to read). */
  loaded: boolean;
  /** The brand's first list read failed (no answer is held): a style may well be active, so an empty `styles` is not
   *  "none". A service without art-style routes answers "none" (not a failure). */
  failed: boolean;
}

const NO_CHOICES: StyleChoices = { styles: [], defaultId: undefined, loaded: false, failed: false };
/** The answer with no brand at all (nothing to read), and a brand whose service has no art styles. */
const NO_STYLES: StyleChoices = { styles: [], defaultId: undefined, loaded: true, failed: false };
/** A brand whose first list read failed. */
const NOT_READ: StyleChoices = { styles: [], defaultId: undefined, loaded: true, failed: true };

/**
 * The art styles a generate sheet can draw a banner in, read for the brand and again each time `readKey` changes (the
 * composer opening): styles created, renamed or made primary on the Art Style page meanwhile must be what the owner
 * picks from. Reads the list only (unlike `useArtStyles`, it never creates a first draft for a brand without styles).
 */
export const useStyleChoices = (tenantId: number | undefined, readKey = 0): StyleChoices => {
  const [held, setHeld] = React.useState<{ tenant: number; choices: StyleChoices } | null>(null);

  // Syncs with spark-creatives: a list read per brand and per `readKey`; the last list holds until the next answers,
  // and a read that fails keeps it (an emptied list would hide the picker and draw a picked draft in the primary).
  React.useEffect(() => {
    if (tenantId === undefined) return undefined;
    const ctl = new AbortController();
    void listArtStyles(tenantId, ctl.signal).then((result) => {
      if (ctl.signal.aborted) return;
      if (!result.ok) {
        const none = result.reason === 'unavailable' ? NO_STYLES : NOT_READ;
        setHeld((prev) => (prev && prev.tenant === tenantId ? prev : { tenant: tenantId, choices: none }));
        return;
      }
      const defined = result.value.filter((item) => item.has_definition);
      const styles = [
        ...defined.filter((item) => item.status === 'active'),
        ...defined.filter((item) => item.status !== 'active'),
      ];
      setHeld({
        tenant: tenantId,
        choices: {
          styles,
          defaultId: styles.find((item) => item.status === 'active')?.id,
          loaded: true,
          failed: false,
        },
      });
    });
    return () => ctl.abort();
  }, [tenantId, readKey]);

  if (tenantId === undefined) return NO_STYLES;
  return held && held.tenant === tenantId ? held.choices : NO_CHOICES;
};

/** One style's cast (its mascots), read from its record; undefined until read (or when its first read failed: a later
 *  failed read keeps the cast). */
export const useStyleCast = (
  tenantId: number | undefined,
  styleId: string | undefined,
  readKey = 0,
): ArtStyleMascot[] | undefined => {
  const key = tenantId !== undefined && styleId ? `${tenantId}|${styleId}` : '';
  const [held, setHeld] = React.useState<{ key: string; mascots: ArtStyleMascot[] | undefined } | null>(null);

  // Syncs with spark-creatives: a record read per picked style, again when `readKey` changes (the composer opening).
  React.useEffect(() => {
    if (!key || tenantId === undefined || !styleId) return undefined;
    const ctl = new AbortController();
    void fetchArtStyle(tenantId, styleId, ctl.signal).then((result) => {
      if (ctl.signal.aborted) return;
      if (result.ok) setHeld({ key, mascots: result.value.mascots });
      else setHeld((prev) => (prev && prev.key === key ? prev : { key, mascots: undefined }));
    });
    return () => ctl.abort();
  }, [key, tenantId, styleId, readKey]);

  return held && held.key === key ? held.mascots : undefined;
};

/** A character pick and the style it was picked for (that style's cast). */
export interface MascotChoice {
  style: string | undefined;
  pick: MascotPick;
}

/** The character a draw uses: the owner's pick while the draw uses the style it was picked for, else Auto (another
 *  style has another cast, e.g. a picked style that fell back to the default). Pure. */
export const pickedMascotOf = (choice: MascotChoice, pickedStyleId: string | undefined): MascotPick =>
  choice.style === pickedStyleId ? choice.pick : MASCOT_AUTO;
